import * as React from 'react'
import {useLocation} from 'react-router'
import {fromMaybe} from '@freckle/maybe'
import useSWR from 'swr'
import {mutate} from 'swr'
import {PATHS} from '@frontrow/console/src/entities/helpers/paths'

import TeachersPage from '@frontrow/console/src/components/pages/teachers-page/teachers-page'
import {
  fetchTeachers,
  deleteTeacherById,
  patchTeacherById,
  sendImpersonateTeacher
} from '@frontrow/console/src/helpers/api-teachers'
import {type TeacherWithAffiliationT} from '@frontrow/console/src/models/teacher'
import {parse} from '@frontrow/console/src/models/teacher'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'
import {parseFilterUrl} from '@frontrow/console/src/helpers/data-table'
import {
  type FetchDataParamsT,
  type FilterT
} from '@frontrow/console/src/components/common/data-table'
import {
  type BaseTeacherAttrs as TeacherT,
  parse as parseTeacherAttrs
} from '@frontrow/console/src/entities/models/teacher'

const TEACHERS_SWR_KEY = 'teachers'

export default function TeachersPageContainer(): React.ReactElement {
  const location = useLocation()
  const defaultFiltered: Array<FilterT> = parseFilterUrl(location)

  const [fetchParams, setFetchParams] = React.useState<FetchDataParamsT | null>(null)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const {
    data: mTeacherData,
    error,
    isLoading
  } = useSWR([TEACHERS_SWR_KEY, fetchParams], async ([_, params]) => {
    const response = await fetchTeachers(params)
    return parse(response)
  })

  const deleteTeacher = React.useCallback(async (teacherId: number): Promise<void> => {
    // Optimistic update - remove teacher from cache
    mutate(
      key => Array.isArray(key) && key[0] === TEACHERS_SWR_KEY,
      (teachers: Array<TeacherWithAffiliationT> | undefined) =>
        teachers?.filter(t => t.teacher.id !== teacherId),
      {revalidate: false}
    )

    try {
      await deleteTeacherById(teacherId)
      // Revalidate all teacher queries
      mutate(key => Array.isArray(key) && key[0] === TEACHERS_SWR_KEY)
    } catch (error: unknown) {
      // Revalidate on error to restore original state
      mutate(key => Array.isArray(key) && key[0] === TEACHERS_SWR_KEY)
      if (error && typeof error === 'object' && 'status' in error) {
        const httpError = error as {status: number}
        if (httpError.status === 403 || httpError.status === 404) {
          throw new Error(
            'You must be logged out of teacher dashboard before deleting a teacher in the console'
          )
        }
      }
      const apiError = firstResponseApiError(error)
      throw new Error(apiError.message)
    }
  }, [])

  const undeleteTeacher = React.useCallback(async (teacherId: number): Promise<TeacherT> => {
    const patch = {deletedAt: null}

    try {
      const updatedTeacher = parseTeacherAttrs(await patchTeacherById(teacherId, patch))

      // Update cache with new teacher data
      mutate(
        key => Array.isArray(key) && key[0] === TEACHERS_SWR_KEY,
        (teachers: Array<TeacherWithAffiliationT> | undefined) =>
          teachers?.map(t => (t.teacher.id === teacherId ? {...t, teacher: updatedTeacher} : t)),
        {revalidate: false}
      )

      return updatedTeacher
    } catch (error) {
      // Revalidate on error
      mutate(key => Array.isArray(key) && key[0] === TEACHERS_SWR_KEY)
      throw error
    }
  }, [])

  const handleImpersonateTeacher = React.useCallback(async (email: string) => {
    const teacherDashWindow = window.open('', '_blank')
    teacherDashWindow!.document.body.innerHTML = 'Authenticating . . .'

    try {
      await sendImpersonateTeacher(email)
      teacherDashWindow!.document.body.innerHTML = 'Loading . . .'
      teacherDashWindow!.location.href = PATHS.teacherDashUrl
    } catch (error: unknown) {
      const apiError = firstResponseApiError(error)
      alert(`Failed to impersonate teacher: ${apiError.message}`)
      teacherDashWindow!.close()
    }
  }, [])

  const fetchData = React.useCallback((fetchDataParams?: FetchDataParamsT | null) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      setFetchParams(fetchDataParams ?? null)
    }, 200)
  }, [])

  const teacherData = fromMaybe(() => [], mTeacherData)

  return (
    <TeachersPage
      fetchData={fetchData}
      teacherData={teacherData}
      isLoading={isLoading}
      error={error?.message || null}
      impersonateTeacher={handleImpersonateTeacher}
      deleteTeacher={deleteTeacher}
      undeleteTeacher={undeleteTeacher}
      defaultFiltered={defaultFiltered}
    />
  )
}
